/**
 * Moteur d'alertes.
 *
 * Une alerte est une **règle déclarée**, pas un affichage conditionnel dispersé
 * dans les gabarits : une clé, une grandeur observée, un seuil, une gravité et
 * une conduite à tenir. Les bornes vivent dans `SEUILS_ALERTES`.
 *
 * Un tableau de bord qui exige d'être lu en entier n'en est pas un : l'écran de
 * tête ne montre que ce qui a franchi un seuil.
 */
import { PLANCHER_TRESORERIE, SEUILS_ALERTES } from './configuration.js';
import { euros, nombre, pourcent } from './format.js';
/**
 * Classement d'une grandeur **décroissante** : plus c'est bas, plus c'est grave.
 * Le cas usuel — une réserve, un taux de recouvrement, une part encaissée.
 */
function classerDecroissant(valeur, seuils) {
    if (valeur <= seuils.alerte)
        return 'alerte';
    if (valeur <= seuils.vigilance)
        return 'vigilance';
    return 'nominal';
}
/**
 * Classement d'une grandeur **croissante** : plus c'est haut, plus c'est grave.
 * Le cas d'un dépassement, d'une ancienneté, d'un compte d'anomalies.
 */
function classerCroissant(valeur, seuils) {
    if (valeur >= seuils.alerte)
        return 'alerte';
    if (valeur >= seuils.vigilance)
        return 'vigilance';
    return 'nominal';
}
/** Évalue toutes les règles et rend les alertes, les plus graves d'abord. */
export function evaluerAlertes(e) {
    const out = [];
    // Le formatage vient du noyau : une seule source de vérité par règle,
    // y compris typographique.
    const eur = (n) => euros(n);
    const pct = (r) => pourcent(r, 0);
    // ── Trésorerie ──────────────────────────────────────────────────────────
    {
        const ratio = PLANCHER_TRESORERIE > 0 ? e.disponibilitesProjetees / PLANCHER_TRESORERIE : 0;
        const g = classerDecroissant(ratio, SEUILS_ALERTES.tresorerie);
        out.push({
            cle: 'tresorerie_plancher',
            domaine: 'tresorerie',
            libelle: 'Disponibilités projetées contre le plancher de trésorerie',
            gravite: g,
            valeur: ratio,
            unite: 'ratio',
            seuils: SEUILS_ALERTES.tresorerie,
            constat: `${eur(e.disponibilitesProjetees)} projetés, soit ${nombre(ratio, 1)} fois le plancher de ${eur(PLANCHER_TRESORERIE)}.`,
            conduite: g === 'nominal'
                ? ''
                : 'Échelonner les décaissements non contraints et accélérer les relances d’encaissement.',
            exercice: e.exercice,
        });
    }
    // ── Recouvrement ────────────────────────────────────────────────────────
    {
        const ratio = e.appelsPrecedents > 0 ? e.appelsCourants / e.appelsPrecedents : 1;
        const g = classerDecroissant(ratio, SEUILS_ALERTES.rythmeAppels);
        out.push({
            cle: 'rythme_appels',
            domaine: 'recouvrement',
            libelle: 'Rythme d’appel des cotisations',
            gravite: g,
            valeur: ratio,
            unite: 'ratio',
            seuils: SEUILS_ALERTES.rythmeAppels,
            constat: `${e.appelsCourants} factures émises contre ${e.appelsPrecedents} à la même date l’an dernier, soit ${pct(ratio)} du rythme.`,
            conduite: g === 'nominal'
                ? ''
                : 'Rattraper le retard de facturation : chaque trimestre de décalage se retrouve dans la trésorerie de fin d’exercice.',
            exercice: e.exercice,
        });
    }
    // ── Ressources conventionnelles ─────────────────────────────────────────
    for (const r of e.ressourcesConventionnelles) {
        if (r.budgete <= 0)
            continue;
        const part = r.realise / r.budgete;
        const brute = classerDecroissant(part, SEUILS_ALERTES.ressourceConventionnelle);
        // Un versement notifié dont la date n'est pas passée n'appelle pas de
        // relance : il se surveille, sans plus.
        const attendu = r.versementNotifie && !r.versementNotifie.echu;
        const g = attendu && brute === 'alerte' ? 'vigilance' : brute;
        if (g === 'nominal')
            continue;
        const v = r.versementNotifie;
        out.push({
            cle: `ressource_${r.code}`,
            domaine: 'ressources',
            libelle: `Encaissement — ${r.libelle}`,
            gravite: g,
            valeur: part,
            unite: 'ratio',
            seuils: SEUILS_ALERTES.ressourceConventionnelle,
            constat: `${eur(r.realise)} encaissés sur ${eur(r.budgete)} ${v ? 'notifiés' : 'budgétés'}, soit ${pct(part)}.`,
            conduite: !v
                ? `Facturer ou relancer ${r.libelle} sans délai ; ${eur(r.budgete - r.realise)} restent à recevoir.`
                : v.echu
                    ? `Le versement notifié de ${r.libelle} était attendu ${v.libelle} : relancer le financeur ; ${eur(r.budgete - r.realise)} restent à recevoir.`
                    : `Versement notifié, attendu ${v.libelle} : vérifier sa réception ; ${eur(r.budgete - r.realise)} restent à recevoir.`,
            exercice: e.exercice,
        });
    }
    // ── Exécution budgétaire ────────────────────────────────────────────────
    for (const p of e.postesCharges) {
        if (p.budgete <= 0)
            continue;
        const taux = p.realise / p.budgete;
        const g = classerCroissant(taux, SEUILS_ALERTES.depassementPoste);
        if (g === 'nominal')
            continue;
        out.push({
            cle: `depassement_${p.code}`,
            domaine: 'execution',
            libelle: `Dépassement — ${p.libelle}`,
            gravite: g,
            valeur: taux,
            unite: 'ratio',
            seuils: SEUILS_ALERTES.depassementPoste,
            constat: `${eur(p.realise)} engagés sur ${eur(p.budgete)} budgétés, soit ${pct(taux)}.`,
            conduite: `Documenter le dépassement de ${eur(p.realise - p.budgete)} et l’imputer à la projection de clôture.`,
            exercice: e.exercice,
        });
    }
    // ── Masse salariale ─────────────────────────────────────────────────────
    {
        const part = e.totalChargesProjetees > 0 ? e.masseSalarialeChargee / e.totalChargesProjetees : 0;
        const g = classerCroissant(part, SEUILS_ALERTES.partMasseSalariale);
        out.push({
            cle: 'part_masse_salariale',
            domaine: 'masse_salariale',
            libelle: 'Part de la masse salariale dans les charges',
            gravite: g,
            valeur: part,
            unite: 'ratio',
            seuils: SEUILS_ALERTES.partMasseSalariale,
            constat: `${eur(e.masseSalarialeChargee)} de masse chargée sur ${eur(e.totalChargesProjetees)} de charges, soit ${pct(part)}.`,
            conduite: g === 'nominal'
                ? ''
                : 'Une masse salariale à ce niveau laisse peu de marge d’ajustement : tout retour à l’équilibre passe par les produits ou par un arbitrage explicite sur cette enveloppe.',
            exercice: e.exercice,
        });
    }
    // ── Réserve ─────────────────────────────────────────────────────────────
    {
        const multiple = e.seuilSecurite > 0 ? e.reserveProjetee / e.seuilSecurite : 0;
        const g = classerDecroissant(multiple, SEUILS_ALERTES.reserve);
        out.push({
            cle: 'reserve_seuil',
            domaine: 'reserve',
            libelle: 'Réserve projetée contre le seuil de sécurité',
            gravite: g,
            valeur: multiple,
            unite: 'ratio',
            seuils: SEUILS_ALERTES.reserve,
            constat: `${eur(e.reserveProjetee)} attendus, soit ${nombre(multiple, 2)} fois le seuil de ${eur(e.seuilSecurite)}.`,
            conduite: g === 'nominal'
                ? ''
                : 'La réserve ne couvrirait plus la rupture du contrat de direction avec le fonds de roulement voulu.',
            exercice: e.exercice,
        });
    }
    // ── Échéance de décision ────────────────────────────────────────────────
    //
    // La règle ne porte pas sur un montant mais sur un **calendrier** : combien
    // de mois reste-t-il avant la date à laquelle il faudra avoir décidé. Elle est
    // décroissante — moins il reste de temps, plus c'est grave — et se déclenche
    // donc avant que la réserve ne baisse.
    if (e.veille) {
        const v = e.veille;
        const g = classerDecroissant(v.moisRestants, SEUILS_ALERTES.echeanceDecisionMois);
        out.push({
            cle: 'echeance_decision',
            domaine: 'reserve',
            libelle: 'Échéance de décision avant franchissement du seuil',
            gravite: g,
            valeur: v.moisRestants,
            unite: 'nombre',
            seuils: SEUILS_ALERTES.echeanceDecisionMois,
            constat: `${v.moisRestants < 0 ? `Échéance du ${v.echeance} dépassée de ${Math.abs(v.moisRestants)} mois` : `${v.moisRestants} mois avant le ${v.echeance}, dernière date utile pour décider`} : ` +
                `« ${v.scenario} » passe sous le seuil en ${v.exercice}` +
                (v.extrapole
                    ? ', par prolongation de son dernier résultat au-delà de la période projetée.'
                    : `, il manquerait ${eur(v.manque)}.`),
            conduite: g === 'nominal'
                ? ''
                : 'Arbitrer avant cette date : renégocier les ressources conventionnelles, ' +
                    'ouvrir la question des effectifs, ou réduire une enveloppe. Passé le délai, ' +
                    'les leviers restants sont plus coûteux.',
            exercice: v.exercice,
        });
    }
    // ── Qualité des données ─────────────────────────────────────────────────
    {
        const g = classerCroissant(e.anciennetteHeures, SEUILS_ALERTES.fraicheurHeures);
        out.push({
            cle: 'fraicheur_donnees',
            domaine: 'donnees',
            libelle: 'Fraîcheur des données servies',
            gravite: g,
            valeur: e.anciennetteHeures,
            unite: 'heures',
            seuils: SEUILS_ALERTES.fraicheurHeures,
            constat: `Dernière ingestion réussie il y a ${Math.round(e.anciennetteHeures)} heures.`,
            conduite: g === 'nominal' ? '' : 'Relancer l’ingestion et vérifier les scopes du jeton Zoho.',
            exercice: e.exercice,
        });
        const ga = classerCroissant(e.nbAnomalies, SEUILS_ALERTES.anomalies);
        out.push({
            cle: 'anomalies_ingestion',
            domaine: 'donnees',
            libelle: 'Anomalies détectées à l’ingestion',
            gravite: ga,
            valeur: e.nbAnomalies,
            unite: 'nombre',
            seuils: SEUILS_ALERTES.anomalies,
            constat: `${e.nbAnomalies} anomalie${e.nbAnomalies > 1 ? 's' : ''} signalée${e.nbAnomalies > 1 ? 's' : ''}.`,
            conduite: ga === 'nominal' ? '' : 'Instruire les anomalies depuis l’écran d’exploitation.',
            exercice: e.exercice,
        });
    }
    const rang = { alerte: 0, vigilance: 1, nominal: 2 };
    return out.sort((a, b) => rang[a.gravite] - rang[b.gravite] || a.cle.localeCompare(b.cle));
}
/** Décompte par gravité, pour la pastille du tableau de bord. */
export function compterAlertes(alertes) {
    return alertes.reduce((acc, a) => ({ ...acc, [a.gravite]: acc[a.gravite] + 1 }), { nominal: 0, vigilance: 0, alerte: 0 });
}
/** Libellé français d'une gravité. */
export function libelleGravite(g) {
    return g === 'alerte' ? 'Alerte' : g === 'vigilance' ? 'Vigilance' : 'Nominal';
}
/** Libellé français d'un domaine. */
export function libelleDomaine(d) {
    const l = {
        tresorerie: 'Trésorerie',
        recouvrement: 'Recouvrement',
        ressources: 'Ressources conventionnelles',
        execution: 'Exécution budgétaire',
        masse_salariale: 'Masse salariale',
        reserve: 'Réserve',
        donnees: 'Qualité des données',
    };
    return l[d];
}
//# sourceMappingURL=alertes.js.map